import { lazy, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Navbar from '../components/navbar/Navbar'
import { matchPath, useLocation, useSearchParams } from 'react-router-dom'
import ROUTES, { ADMIN_ROUTES, O_AUTH_ROUTES, UNPROTECTED_ROUTES } from '../assets/routes'
import { useDispatch, useSelector } from 'react-redux'
import OrderStatusAlert from '../components/OrderStatusAlert/OrderStatusAlert'
import './styles.css'
import './main.css'
import { getConfiguration } from '../services/apis/supercharts'
import { isAuthenticated } from '../util/authentication'
import { Box, SxProps } from '@mui/material'
import { selectFeatureRestrictionDialog, selectPrimaryTradingAccount, selectUserDetails } from '../selectors/user'
import { darkBg, primary } from '../assets/colors'
import Tour from '../components/tour/tour'
import { selectAlertModalData } from '../selectors/alerts'
import AlertModal from '../components/alerts/alertModal'
import AlertsToastGroup from '../components/alerts/alertsToastGroup/alertsToastGroup'
import { fetchCompanyEvents } from '../util/companyEvents'
import { useResizeDetector } from 'react-resize-detector'
import SideBarLeft from '../components/sideBarLeft/SideBarLeft'
import SideBarRight from '../components/sideBarRight/SideBarRight'
import { AiAssistantProvider } from '../components/aiAssistant/AiAssistantContext'
import { isAccountRoute } from './settings/accountRoutes'
import MainContentLayout from './mainContentLayout'
import { selectRightSidebarWidget } from '../selectors/common'
import { selectIsEmbedded } from '../selectors/device'
import { isWidgetbarLayoutWidget } from '../util/sidebar'
import { refetchPositionsAndOrders } from '../services/apis/tradingAccount'
import { fetchUserAlerts } from '../services/apis/alerts'
import { updateOrderHistory } from '../reducer/orders.reducer'
import { WebSocketResponseMessage } from '../proto/generated/websocket'
import { WebSocketContext } from '../components/webSocketConnection/WebSocket'
import FeatureRestrictionDialog from '../components/featureRestrictionDialog/FeatureRestrictionDialog'
import { Group, setFeatureRestrictionDialog } from '../reducer/user.reducer'
import { useUser } from '../context/UserContext'
import { FeatureCode } from '../util/RestrictionFeatureCode'
import FeedbackCaptureLayer from '../components/support/FeedbackCaptureLayer'
const RIGHT_SIDEBAR_USER_WIDTH_KEY = 'rightSidebarUserWidth'
const DEFAULT_RIGHT_SIDEBAR_WIDTH = 440

const readStoredRightSidebarWidth = (): number | null => {
    try {
        const saved = localStorage.getItem(RIGHT_SIDEBAR_USER_WIDTH_KEY)
        if (!saved) return null
        const parsed = parseInt(saved, 10)
        return Number.isFinite(parsed) ? parsed : null
    } catch {
        return null
    }
}

const Dashboard = ({ children }: any) => {
    const ws = useContext(WebSocketContext)
    const location = useLocation()
    const dispatch = useDispatch()
    const { width, ref } = useResizeDetector()
    const alertModalData = useSelector(selectAlertModalData)
    const userDetails = useSelector(selectUserDetails)
    const [searchParams] = useSearchParams()
    const authenticated = isAuthenticated()
    const rightSideWidget = useSelector(selectRightSidebarWidget)
    const isEmbedded = useSelector(selectIsEmbedded)
    const userSetWidth = useRef<number | null>(readStoredRightSidebarWidth())
    const [rightWidth, setRightWidth] = useState(
        () => userSetWidth.current ?? DEFAULT_RIGHT_SIDEBAR_WIDTH
    )
    const isDragging = useRef(false)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const prevPositionsAccount = useRef<string>('')
    const prevOrdersAccount = useRef<string>('')
    const firstLoadOrdersAndPositions = useRef(false)
    const userAlertsFetched = useRef(false)
    const {
        user: { simulatorAccessible, scalperAccessible, multiStrikeChartAccessible }
    } = useUser()
    const featureRestrictionDialogData = useSelector(selectFeatureRestrictionDialog)
    const route = useLocation().pathname

    useEffect(() => {
        setTimeout(() => {
            if (authenticated) {
                fetchCompanyEvents(dispatch)
            }
        }, 2000)
    }, [])

    useEffect(() => {
        if (!authenticated || userAlertsFetched.current) return
        userAlertsFetched.current = true
        setTimeout(() => {
            fetchUserAlerts({ dispatch }).catch(() => {
                userAlertsFetched.current = false
            })
        }, 2000)
    }, [authenticated, dispatch])

    // Fetch configuration for supercharts
    useEffect(() => {
        if (isEmbedded && authenticated) {
            getConfiguration({ dispatch })
        }
    }, [authenticated, isEmbedded])

    useEffect(() => {
        const utmParams: Record<string, string> = {}
        const newSearchParams = new URLSearchParams(searchParams.toString())
        for (const [key, value] of searchParams.entries()) {
            if (key.includes('utm_')) {
                utmParams[key] = value
                newSearchParams.delete(key)
            }
        }
        if (Object.keys(utmParams).length > 0) {
            if (![...UNPROTECTED_ROUTES, ...ADMIN_ROUTES].includes(location.pathname)) {
                sessionStorage.setItem('prevUrl', location.pathname + location.search)
            }
            window.sessionStorage.setItem('utm_params', Object.keys(utmParams).length > 0 ? JSON.stringify(utmParams) : '')
        }
    }, [])

    useEffect(() => {
        if (![...UNPROTECTED_ROUTES, ...O_AUTH_ROUTES, ...ADMIN_ROUTES].includes(location.pathname)) {
            sessionStorage.setItem('prevUrl', location.pathname + location.search)
        }
    }, [location.pathname, location.search])

    useEffect(() => {
        if (![...UNPROTECTED_ROUTES, ...O_AUTH_ROUTES, ...ADMIN_ROUTES].includes(location.pathname)) {
            sessionStorage.setItem('prevUrl', location.pathname + location.search)
        }
    }, [location.pathname, location.search])

    const handleCloseRestrictionDialog = useCallback(() => {
        dispatch(
            setFeatureRestrictionDialog({
                open: false,
                feature: '',
                group: undefined
            })
        )
    }, [])

    useEffect(() => {
        if (!simulatorAccessible && matchPath(ROUTES.simulator, route)) {
            dispatch(
                setFeatureRestrictionDialog({
                    open: true,
                    feature: 'Simulator',
                    group: Group.SIMULATOR,
                    featureRestrictionCode: FeatureCode.SIMULATOR_FULL_ACCESS
                })
            )
        } else if (!multiStrikeChartAccessible && matchPath(ROUTES.multiStrikeChart, route)) {
            dispatch(
                setFeatureRestrictionDialog({
                    open: true,
                    feature: 'Multi Strike Chart',
                    group: Group.MULTI_STRIKE,
                    featureRestrictionCode: FeatureCode.MULTI_STRIKE_FULL_ACCESS
                })
            )
        } else if (!scalperAccessible && matchPath(ROUTES.scalper, route)) {
            dispatch(
                setFeatureRestrictionDialog({
                    open: true,
                    feature: 'Scalper',
                    featureRestrictionCode: FeatureCode.SCALPER_FULL_ACCESS,
                    group: Group.SCALPER

                })
            )
        } else {
            handleCloseRestrictionDialog()
        }
    }, [simulatorAccessible, handleCloseRestrictionDialog, dispatch, route])

    const showLayout = [
        ROUTES.base, ROUTES.symbol, ROUTES.chart, ROUTES.spread,ROUTES.oiOverview, ROUTES.optionChain, ROUTES.openInterest, ROUTES.openInterestChange, ROUTES.multiStrikeChart,
        ROUTES.seasonals, ROUTES.technicals, ROUTES.components, ROUTES.financials,
        ROUTES.screener, ROUTES.heatmap, ROUTES.fiiDii, ROUTES.fiiDiiSummary, ROUTES.fiiDiiFuturesOptions,
        ROUTES.fiiDiiCashMarket, ROUTES.fiiDiiFiiHistory, ROUTES.strategyBuilder, ROUTES.simulator, ROUTES.reports,
        ROUTES.tradingAccount, ROUTES.automation, ROUTES.tradingAccountConnect, ROUTES.scalper, ROUTES.strategy,
        ROUTES.shareholderHoldings,ROUTES.industries,ROUTES.industriesLevel].reduce((show, route) => {
            return show || !!matchPath(route, location.pathname)
        }, isAccountRoute(location.pathname))

    const tourDisplayStyles = useMemo(() => ({ backdropColor: darkBg, borderWidth: 2, borderColor: primary }), [])

    const gridStyle: SxProps = useMemo(() => {
        if (isEmbedded) {
            return {
                position: 'relative',
                display: 'grid',
                height: 'max-content',
                maxHeight: '100vh',
                minHeight: '100vh',
                gridTemplateRows: '1fr',
                gridTemplateAreas: `
                  "main-content"
                `,
                rowGap: '0px',
                gridTemplateColumns: '1fr'
            }
        }

        // useResizeDetector width is undefined on first paint; fall back so open sidebar panel gets width immediately
        const viewportWidth = width ?? (typeof window !== 'undefined' ? window.innerWidth : 1920)
        const sidebarPanelWidth = viewportWidth > 1280 ? rightWidth : 0
        const widgetbarPanelOpen = isWidgetbarLayoutWidget(rightSideWidget)

        return {
            position: 'relative',
            display: 'grid',
            height: 'max-content',
            maxHeight: '100vh',
            minHeight: '100vh',
            gridTemplateRows: 'auto minmax(0, 1fr)',
            gridTemplateAreas: widgetbarPanelOpen
                ? `
              "header header header header header header"
              "left-sidebar drawer main-content resize-drawer widgetbar-pages right-sidebar"
            `
                : `
              "header header header header header"
              "left-sidebar drawer main-content widgetbar-pages right-sidebar"
            `,
            rowGap: 'var(--extra-small-spacing)',
            gridTemplateColumns: widgetbarPanelOpen
                ? `56px 0 minmax(0px, 1fr) var(--extra-small-spacing) ${sidebarPanelWidth}px 56px`
                : '56px 0 minmax(0px, 1fr) 0 56px'
        }
    }, [isEmbedded, rightSideWidget, rightWidth, width])

    const startDragging = () => {
        isDragging.current = true
        document.body.style.userSelect = 'none'
        document.body.style.pointerEvents = 'none'
        document.body.style.cursor = 'ew-resize'
    }

    const stopDragging = () => {
        isDragging.current = false
        document.body.style.userSelect = 'auto'
        document.body.style.pointerEvents = 'auto'
        document.body.style.cursor = 'auto'
    }

    const onDrag = useCallback((e: MouseEvent) => {
        if (!isDragging.current) return
        document.body.style.cursor = 'ew-resize'
        const value = window.innerWidth - e.clientX - 56
        const newWidth = Math.min(800, Math.max(350, value))
        userSetWidth.current = newWidth
        localStorage.setItem(RIGHT_SIDEBAR_USER_WIDTH_KEY, newWidth.toString())
        setRightWidth(newWidth)
    }, [])

    useEffect(() => {
        window.addEventListener('mousemove', onDrag)
        window.addEventListener('mouseup', stopDragging)
        return () => {
            window.removeEventListener('mousemove', onDrag)
            window.removeEventListener('mouseup', stopDragging)
        }
    }, [onDrag])

    useEffect(() => {
        if (!primaryTradingAccount) return
        const accountId = primaryTradingAccount.accountId
        const timeoutId = setTimeout(() => {
            void refetchPositionsAndOrders()
            firstLoadOrdersAndPositions.current = true
            prevPositionsAccount.current = accountId
            prevOrdersAccount.current = accountId
        }, 1000)
        return () => clearTimeout(timeoutId)
    }, [primaryTradingAccount])

    useEffect(() => {
        if (firstLoadOrdersAndPositions.current && primaryTradingAccount && prevPositionsAccount.current !== primaryTradingAccount.accountId) {
            void refetchPositionsAndOrders(undefined, true)
            prevPositionsAccount.current = primaryTradingAccount.accountId
        }
        if (firstLoadOrdersAndPositions.current && primaryTradingAccount && prevOrdersAccount.current !== primaryTradingAccount.accountId) {
            prevOrdersAccount.current = primaryTradingAccount.accountId
        }
    }, [dispatch, primaryTradingAccount])

    const onWebSocketMessageReceived = useCallback(
        (_data: Uint8Array, responseData: WebSocketResponseMessage) => {
            if (responseData?.orderUpdate?.orders?.length && responseData?.orderUpdate?.orders?.length > 0 && responseData.orderUpdate.tradingAccountId === primaryTradingAccount?.accountId) {
                dispatch(updateOrderHistory({ updates: responseData.orderUpdate.orders }))
            }
        },
        [dispatch, primaryTradingAccount]
    )

    useEffect(() => {
        if (ws !== null) {
            ws!.addListener(onWebSocketMessageReceived)
        }
        return () => {
            if (ws !== null) {
                ws!.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    const isRestrictionDialogClosable = useCallback(() => {
        return !(
            (!simulatorAccessible && matchPath(ROUTES.simulator, route)) ||
            (!multiStrikeChartAccessible && matchPath(ROUTES.multiStrikeChart, route)) ||
            (!scalperAccessible && matchPath(ROUTES.scalper, route))
        )
    }, [simulatorAccessible, multiStrikeChartAccessible, scalperAccessible, route])

    return (
        <>
            {showLayout && isAuthenticated() ? (
                    <AiAssistantProvider>
                    <Box ref={ref} sx={gridStyle} className="dashboard-box">
                        {!isEmbedded && <Navbar />}
                        {!isEmbedded && <SideBarLeft />}
                        {!isEmbedded ? <FeedbackCaptureLayer /> : null}
                        {!isEmbedded && (
                            <div style={{ gridArea: 'drawer', position: 'relative' }}>
                                <AlertsToastGroup />
                            </div>
                        )}
                        {!isEmbedded && isWidgetbarLayoutWidget(rightSideWidget) && (
                            <Box
                                className="divider"
                                style={{
                                    gridArea: 'resize-drawer',
                                    cursor: 'ew-resize',
                                    zIndex: 1000
                                }}
                                onMouseDown={startDragging}
                            />
                        )}
                        {!isEmbedded && <SideBarRight blur={featureRestrictionDialogData.open} />}
                        <MainContentLayout embedded={isEmbedded}>
                            <Box sx={{ ...(featureRestrictionDialogData.open ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}), height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                {children}
                            </Box>
                        </MainContentLayout>
                        {!isEmbedded && <OrderStatusAlert />}
                        {/* {!!(
                            userDetails?.currentUser &&
                            (() => {
                                try {
                                    const attributes = JSON.parse(userDetails.currentUser.attributes)
                                    return !attributes?.guidedTourShown
                                } catch (error) {
                                    console.error('Failed to parse attributes JSON', error)
                                    return true
                                }
                            })()
                        ) && <TourSlideShow />} */}
                        {!isEmbedded && alertModalData && <AlertModal />}
                        <Tour displayStyles={tourDisplayStyles} />
                        <FeatureRestrictionDialog
                            feature={featureRestrictionDialogData.feature}
                            featureRestrictionCode={featureRestrictionDialogData.featureRestrictionCode}
                            open={featureRestrictionDialogData.open}
                            onClose={isRestrictionDialogClosable() ? handleCloseRestrictionDialog : undefined}
                            group={featureRestrictionDialogData.group}
                        />
                    </Box>
                    </AiAssistantProvider>
            ) : (
                children
            )}
        </>
    )
}

export default memo(Dashboard)